/**
 * Storage that never throws.
 *
 * Inside a sandboxed iframe (which is how Wix embeds dashboard pages) and in
 * Safari private mode, touching `localStorage` throws a SecurityError — which
 * would otherwise crash the app on boot, before anything renders.
 *
 * Falls back localStorage -> sessionStorage -> in-memory, so the app always has
 * somewhere to keep the auth token for the life of the page.
 */

const memory = new Map();

const probe = (store) => {
  try {
    const k = '__zs_probe__';
    store.setItem(k, '1');
    store.removeItem(k);
    return true;
  } catch {
    return false;
  }
};

let backing = null;

const resolveBacking = () => {
  if (backing) return backing;

  if (typeof window !== 'undefined') {
    try {
      if (window.localStorage && probe(window.localStorage)) {
        backing = window.localStorage;
        return backing;
      }
    } catch { /* blocked entirely */ }

    try {
      if (window.sessionStorage && probe(window.sessionStorage)) {
        backing = window.sessionStorage;
        return backing;
      }
    } catch { /* blocked entirely */ }
  }

  backing = {
    getItem: (k) => (memory.has(k) ? memory.get(k) : null),
    setItem: (k, v) => memory.set(k, String(v)),
    removeItem: (k) => memory.delete(k),
  };
  return backing;
};

export const safeStorage = {
  get(key) {
    try {
      return resolveBacking().getItem(key);
    } catch {
      return memory.has(key) ? memory.get(key) : null;
    }
  },

  set(key, value) {
    try {
      resolveBacking().setItem(key, value);
    } catch {
      memory.set(key, String(value));
    }
  },

  remove(key) {
    try {
      resolveBacking().removeItem(key);
    } catch {
      memory.delete(key);
    }
  },

  /** True when a real browser store is available (not the memory fallback). */
  isPersistent() {
    const b = resolveBacking();
    return b === (typeof window !== 'undefined' ? window.localStorage : null)
      || b === (typeof window !== 'undefined' ? window.sessionStorage : null);
  },
};

export default safeStorage;
